export type Role =
  | 'PENDING'
  | 'REQUESTER'
  | 'APPROVER'
  | 'ADMIN'
  | 'SUPER_ADMIN'
  | 'SYSTEM_ADMIN'
  | 'MTN_TECHNICIAN'
  | 'APPROVED_VIEWER';

export type CurrentUser = {
  id: string;
  email: string;
  displayName: string;
  jobTitle: string | null;
  role: Role;
  department: { id: string; name: string } | null;
  grants: string[];
  isActive: boolean;
  tenantId: string;
  lastLoginAt: string | null;
  walkthroughSeenAt: string | null;
};

export type AdminUser = {
  id: string;
  email: string;
  displayName: string;
  jobTitle: string | null;
  role: Role;
  tenantId: string;
  isActive: boolean;
  department: { id: string; name: string } | null;
  selfApprove: boolean;
  lastLoginAt: string | null;
  /** False when you may not change this account at all. */
  manageable: boolean;
  /** False once the account is part of any request's history; deactivate instead. */
  deletable: boolean;
  historyNote: string | null;
  walkthroughSeenAt: string | null;
};

export type Department = { id: string; name: string; code: string | null };

export type FeedbackSummary = {
  count: number;
  average: number | null;
  distribution: [number, number, number, number, number];
  recent: { count: number; average: number | null };
};

export type FeedbackEntry = {
  id: string;
  rating: number;
  comment: string | null;
  path: string | null;
  createdAt: string;
  user: { displayName: string; email: string } | null;
};

export type CatalogCategory =
  | 'HARDWARE'
  | 'SYSTEM'
  | 'REMOTE_ACCESS'
  | 'SHAREPOINT_SITE'
  | 'FILESHARE_FOLDER'
  | 'LOCATION'
  | 'SHARED_MAILBOX'
  | 'PRINTER';

export type AccessLevel = 'R' | 'RW';

export type CatalogItem = {
  id: string;
  category: CatalogCategory;
  name: string;
  description: string | null;
  legacyNumber: number | null;
  defaultAccessLevel: AccessLevel | null;
  requiresAssetDetails: boolean;
  isCompulsory: boolean;
};

export type RequestStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'CHANGES_REQUESTED'
  | 'APPROVED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED';

export type EmploymentType = 'FIXED_TERM' | 'PERMANENT';
export type ExitReason = 'RESIGNATION' | 'TERMINATION' | 'CONTRACT_END' | 'RETIREMENT' | 'OTHER';
export type MailboxAction = 'DISABLE' | 'DELETE' | 'CONVERT_TO_SHARED';
export type ReturnStatus = 'RETURNED' | 'NOT_RETURNED' | 'NOT_APPLICABLE';

export type RequestItem = {
  catalogItemId: string;
  category: CatalogCategory;
  name: string;
  legacyNumber: number | null;
  isCompulsory: boolean;
  required: boolean;
  accessLevel: AccessLevel | null;
  assetDetails: string | null;
  notes: string | null;
  implementedAt?: string | null;
  /** Offboarding: false means keep it for a stated handover period. */
  revoke?: boolean | null;
  returnStatus?: ReturnStatus | null;
  returnCondition?: string | null;
};

export type Implementation = {
  technicianName: string | null;
  createdEmailAddress: string | null;
  implementationNotes: string | null;
  completedDate: string | null;
};

export type Problem = { field: string; message: string };

export type SignatureAttestation = {
  stage: 'REQUEST' | 'APPROVAL' | 'IMPLEMENTATION';
  signerName: string;
  signerEmail: string;
  signerRole: Role;
  signedAt: string;
  payloadSha256: string;
  voidedAt: string | null;
};

export type RequestDetail = {
  id: string;
  reference: string;
  type: 'ENROLMENT' | 'OFFBOARDING';
  status: RequestStatus;
  requestedBy: { id: string; displayName: string; email: string };
  approver: { id: string; displayName: string; email: string } | null;
  /** Set once pushed to the onsite tech; doneAt once the IT side ticks it. */
  onsite: { pushedAt: string; pushedBy: string | null; note: string | null; doneAt: string | null; doneBy: string | null } | null;
  submittedAt: string | null;
  updatedAt: string;
  createdAt: string;
  employee: {
    fullName: string;
    idPassport: string | null;
    jobTitle: string | null;
    departmentId: string | null;
    departmentName: string | null;
    managerName: string | null;
    startDate: string | null;
    employmentType: EmploymentType | null;
    contractEndDate: string | null;
    takingOverExistingAccount: boolean | null;
    takeoverAccountDetails: string | null;
    locationType: 'ONSITE' | 'OFFICE' | 'OTHER' | null;
    locationOther: string | null;
    contactNumber: string | null;
    emailRequired: boolean;
    emailAddress: string | null;

    lastWorkingDay: string | null;
    exitReason: ExitReason | null;
    exitReasonOther: string | null;
    immediateExit: boolean | null;
    mailboxAction: MailboxAction | null;
    mailboxRetentionDays: number | null;
    outOfOfficeMessage: string | null;
    forwardToName: string | null;
    forwardUntil: string | null;
    releaseLicences: boolean | null;
    dataHandoverTo: string | null;
    sharedMailboxHandoverTo: string | null;
    teamsHandoverTo: string | null;
  } | null;
  items: RequestItem[];
  supersedes: { id: string; reference: string; completedAt: string | null } | null;
  /** The completed enrolment this one adds to ("more access"), with what it already granted. */
  amends: {
    id: string;
    reference: string;
    completedAt: string | null;
    alreadyGranted: { catalogItemId: string; accessLevel: 'R' | 'RW' | null }[];
  } | null;
  /** Later "more access" requests raised against this one. */
  followUps: { id: string; reference: string; status: RequestStatus; createdAt: string; completedAt: string | null }[];
  specialInstructions: string | null;
  signatures: SignatureAttestation[];
  approvalComments: { body: string; createdAt: string }[];
  selfApproved: boolean;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  reviewedBy: { id: string; displayName: string } | null;
  admin: { id: string; displayName: string; email: string } | null;
  implementation: Implementation | null;
  completedAt: string | null;
  problems: Problem[];
};

export type ItQueueRow = RequestListRow & {
  inProgress: boolean;
  assignedTo: string | null;
  approvedAt: string | null;
  doneCount: number;
};

export type QueueRow = RequestListRow & { isOwn: boolean; reviewedBy: string | null };

export type Summary = {
  mine: { drafts: number; changesRequested: number; awaitingApproval: number; withIt: number; completed: number };
  needsMe: RequestListRow[];
  approval: { waiting: number; oldestSubmittedAt: string | null; next: RequestListRow | null } | null;
  it: {
    toPickUp: number;
    inProgress: number;
    mineInProgress: number;
    pendingUsers: number;
    next: RequestListRow | null;
  } | null;
};

export type RequestListRow = {
  id: string;
  reference: string;
  type: 'ENROLMENT' | 'OFFBOARDING';
  status: RequestStatus;
  employeeName: string | null;
  idPassportMasked: string;
  department: string | null;
  startDate: string | null;
  itemCount: number;
  requestedBy: string;
  updatedAt: string;
  submittedAt: string | null;
};

export type DraftPatch = ReturnType<typeof import('./wizard/draft').draftToPatch>;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });

  if (!response.ok) {
    let code = 'error';
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string; message?: string };
      code = body.error ?? code;
      message = body.message ?? message;
    } catch {
      /* response had no JSON body */
    }
    throw new ApiError(response.status, code, message);
  }

  return (await response.json()) as T;
}

export type AppNotification = {
  id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
  request: { id: string; reference: string } | null;
};

export type NudgeState = { canNudge: boolean; nextAllowedAt: string | null };

export type RequestMessage = { id: string; body: string; createdAt: string; authorName: string; mine: boolean };

export type CatalogAdminItem = {
  id: string;
  name: string;
  description: string | null;
  legacyNumber: number | null;
  defaultAccessLevel: AccessLevel | null;
  requiresAssetDetails: boolean;
  isCompulsory: boolean;
  isActive: boolean;
  sortOrder: number;
  usedBy: number;
};

export type CatalogAdminCategory = {
  key: string;
  label: string;
  supportsAccessLevel: boolean;
  supportsAssetDetails: boolean;
  supportsLegacyNumber: boolean;
  items: CatalogAdminItem[];
};

export type AdminDepartment = {
  id: string;
  name: string;
  code: string | null;
  isActive: boolean;
  userCount: number;
  usedBy: number;
};

export type NewCatalogItem = {
  category: string;
  name: string;
  description?: string | null;
  legacyNumber?: number | null;
  defaultAccessLevel?: AccessLevel | null;
  requiresAssetDetails?: boolean;
  isCompulsory?: boolean;
};

export type CatalogItemPatch = Partial<Omit<NewCatalogItem, 'category'>> & { isActive?: boolean };

export type EnrolmentOption = {
  id: string;
  reference: string;
  employeeName: string | null;
  department: string | null;
  jobTitle: string | null;
  completedAt: string | null;
  itemCount: number;
};

export type ArchiveRow = {
  id: string;
  reference: string;
  type: 'ENROLMENT' | 'OFFBOARDING';
  status: RequestStatus;
  employeeName: string | null;
  idPassportMasked: string | null;
  department: string | null;
  requestedBy: string;
  approver: string | null;
  admin: string | null;
  itemCount: number;
  createdAt: string;
  submittedAt: string | null;
  completedAt: string | null;
  hasDocument: boolean;
};

export type ArchiveFilters = {
  q?: string;
  type?: string;
  status?: string;
  departmentId?: string;
  from?: string;
  to?: string;
  page?: number;
};

export type ArchivePage = {
  total: number;
  page: number;
  pageSize: number;
  pages: number;
  counts: Record<string, number>;
  requests: ArchiveRow[];
};

// --- Audit trail -----------------------------------------------------------

export type AuditEvent = {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actor: { name: string; email: string } | null;
  before: unknown;
  after: unknown;
  ip?: string | null;
  createdAt: string;
};

export type AuditFilters = {
  actorUserId?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  from?: string;
  to?: string;
  page?: number;
};

export type AuditPage = {
  total: number;
  page: number;
  pageSize: number;
  pages: number;
  events: AuditEvent[];
  actions: { action: string; count: number }[];
  actors: { id: string; name: string }[];
};

// --- System settings -------------------------------------------------------

export type SystemSettings = {
  mailFromName: string | null;
  mailReplyTo: string | null;
  nudgeIntervalHours: number;
  pdfHeaderText: string | null;
  pdfFooterText: string | null;
  retentionMonths: number | null;
  onsiteTechEmail: string | null;
  updatedAt: string;
  updatedBy: string | null;
};

export type MailLog = {
  lastSevenDays: { sent: number; failed: number; mailOff: number };
  entries: {
    id: string;
    type: string;
    title: string;
    to: { displayName: string; email: string };
    createdAt: string;
    emailedAt: string | null;
    deliveryError: string | null;
  }[];
};

export type SettingsResponse = {
  settings: SystemSettings;
  limits: {
    nudgeIntervalHours: { min: number; max: number };
    retentionMonths: { min: number; max: number };
  };
  /** Read-only, and shown as such: these come from the server's configuration. */
  environment: { mailFromAddress: string | null; mailEnabled: boolean; mailProblem: string | null; pdfAvailable: boolean };
};

function queryString(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '' && value !== null) params.set(key, String(value));
  }
  return params.toString();
}

export const api = {
  me: () => request<{ user: CurrentUser }>('/api/me'),
  adminUsers: () =>
    request<{ users: AdminUser[]; assignableRoles: Role[]; canDeleteUsers: boolean }>('/api/admin/users'),
  deleteUser: (id: string, body: { reason: string; confirmEmail: string }) =>
    request<{ ok: true; email: string }>(`/api/admin/users/${id}`, { method: 'DELETE', body: JSON.stringify(body) }),
  departments: () => request<{ departments: Department[] }>('/api/departments'),
  updateUser: (id: string, patch: Partial<{ role: Role; departmentId: string | null; isActive: boolean; selfApprove: boolean }>) =>
    request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  logout: () => request<{ redirectTo: string }>('/api/auth/logout', { method: 'POST' }),

  walkthroughSeen: () => request<{ ok: true }>('/api/me/walkthrough-seen', { method: 'POST' }),
  walkthroughReset: () => request<{ ok: true }>('/api/me/walkthrough-reset', { method: 'POST' }),
  remindAccess: () => request<{ sent: boolean; nextAt: string | null }>('/api/me/remind-access', { method: 'POST' }),

  pushOnsite: (id: string, note: string) =>
    request<{ ok: true; to: string }>(`/api/requests/${id}/onsite/push`, { method: 'POST', body: JSON.stringify({ note }) }),
  onsiteDone: (id: string) => request<{ ok: true }>(`/api/requests/${id}/onsite/done`, { method: 'POST' }),

  sendFeedback: (body: { rating: number; comment?: string; path?: string }) =>
    request<{ ok: true }>('/api/feedback', { method: 'POST', body: JSON.stringify(body) }),
  adminFeedback: () => request<{ summary: FeedbackSummary; entries: FeedbackEntry[] }>('/api/admin/feedback'),
  resetUserWalkthrough: (id: string) =>
    request<{ ok: true }>(`/api/admin/users/${id}/walkthrough-reset`, { method: 'POST' }),

  catalog: () => request<{ items: CatalogItem[] }>('/api/catalog'),

  adminCatalog: () => request<{ categories: CatalogAdminCategory[] }>('/api/admin/catalog'),

  createCatalogItem: (item: NewCatalogItem) =>
    request<{ item: CatalogAdminItem }>('/api/admin/catalog', { method: 'POST', body: JSON.stringify(item) }),

  updateCatalogItem: (id: string, patch: CatalogItemPatch) =>
    request<{ item: CatalogAdminItem; appliedTo?: number }>(`/api/admin/catalog/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  moveCatalogItem: (id: string, direction: 'up' | 'down') =>
    request<{ ok: true; moved: boolean }>(`/api/admin/catalog/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ direction }),
    }),

  deleteCatalogItem: (id: string) => request<{ ok: true }>(`/api/admin/catalog/${id}`, { method: 'DELETE' }),

  adminDepartments: () => request<{ departments: AdminDepartment[] }>('/api/admin/departments'),

  createDepartment: (name: string, code: string | null) =>
    request<{ department: AdminDepartment }>('/api/admin/departments', {
      method: 'POST',
      body: JSON.stringify({ name, code }),
    }),

  updateDepartment: (id: string, patch: Partial<{ name: string; code: string | null; isActive: boolean }>) =>
    request<{ department: AdminDepartment }>(`/api/admin/departments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteDepartment: (id: string) => request<{ ok: true }>(`/api/admin/departments/${id}`, { method: 'DELETE' }),

  enrolments: (q: string) =>
    request<{ enrolments: EnrolmentOption[] }>(`/api/requests/enrolments?q=${encodeURIComponent(q)}`),

  linkEnrolment: (id: string, enrolmentId: string | null) =>
    request<{ request: RequestDetail; canEdit: boolean }>(`/api/requests/${id}/link-enrolment`, {
      method: 'POST',
      body: JSON.stringify({ enrolmentId }),
    }),

  audit: (filters: AuditFilters) => request<AuditPage>(`/api/audit?${queryString(filters)}`),

  auditExportUrl: (filters: AuditFilters) => `/api/audit/export.csv?${queryString(filters)}`,

  requestActivity: (id: string) => request<{ events: AuditEvent[] }>(`/api/requests/${id}/activity`),

  settings: () => request<SettingsResponse>('/api/settings'),
  mailLog: () => request<MailLog>('/api/settings/mail-log'),
  mailTest: () => request<{ ok: true; to: string }>('/api/settings/mail-test', { method: 'POST' }),
  feedbackMine: () => request<{ lastRatedAt: string | null }>('/api/feedback/mine'),

  saveSettings: (patch: Partial<Omit<SystemSettings, 'updatedAt' | 'updatedBy'>>) =>
    request<{ settings: SystemSettings; changed: string[] }>('/api/settings', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  archive: (filters: ArchiveFilters) => request<ArchivePage>(`/api/archive?${queryString(filters)}`),

  archiveExportUrl: (filters: ArchiveFilters) => `/api/archive/export.csv?${queryString(filters)}`,

  archiveExportDocumentsUrl: (ids: string[]) => `/api/archive/export-documents.zip?ids=${ids.join(',')}`,

  listRequests: (scope: 'mine' | 'all' | 'approved' = 'mine') =>
    request<{ requests: RequestListRow[] }>(`/api/requests?scope=${scope}`),

  createRequest: (type: 'ENROLMENT' | 'OFFBOARDING' = 'ENROLMENT') =>
    request<{ request: RequestDetail }>('/api/requests', { method: 'POST', body: JSON.stringify({ type }) }),

  getRequest: (id: string) => request<{ request: RequestDetail; canEdit: boolean }>(`/api/requests/${id}`),

  moreAccess: (id: string) =>
    request<{ request: RequestDetail }>(`/api/requests/${id}/more-access`, { method: 'POST' }),
  cloneRequest: (id: string) =>
    request<{ request: RequestDetail }>(`/api/requests/${id}/clone`, { method: 'POST' }),

  patchRequest: (id: string, patch: DraftPatch) =>
    request<{ request: RequestDetail; canEdit: boolean }>(`/api/requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  notifications: () => request<{ unread: number; notifications: AppNotification[] }>('/api/notifications'),

  markNotificationsRead: (ids: string[] | 'all') =>
    request<{ ok: true; count: number }>('/api/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  nudgeState: (id: string) => request<NudgeState>(`/api/requests/${id}/nudge`),

  nudge: (id: string) =>
    request<{ ok: true; recipientCount: number; targetRole: Role; nextAllowedAt: string }>(
      `/api/requests/${id}/nudge`,
      { method: 'POST' },
    ),

  requestMessages: (id: string) => request<{ messages: RequestMessage[] }>(`/api/requests/${id}/messages`),

  sendRequestMessage: (id: string, body: string) =>
    request<RequestMessage>(`/api/requests/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  cancelRequest: (id: string) => request<{ ok: true }>(`/api/requests/${id}/cancel`, { method: 'POST' }),

  /** Super Admins and above only, and the only irreversible call in this API. */
  deleteRequest: (id: string, body: { reason: string; confirmReference: string }) =>
    request<{ ok: true; reference: string; signaturesDestroyed: number }>(`/api/requests/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),

  queue: () => request<{ requests: QueueRow[]; canSelfApprove: boolean }>('/api/requests/queue'),

  submitRequest: (id: string, signature: string) =>
    request<{ ok: true }>(`/api/requests/${id}/submit`, { method: 'POST', body: JSON.stringify({ signature }) }),

  approveRequest: (id: string, signature: string) =>
    request<{ ok: true }>(`/api/requests/${id}/approve`, { method: 'POST', body: JSON.stringify({ signature }) }),

  returnRequest: (id: string, comment: string) =>
    request<{ ok: true }>(`/api/requests/${id}/return`, { method: 'POST', body: JSON.stringify({ comment }) }),

  rejectRequest: (id: string, comment: string) =>
    request<{ ok: true }>(`/api/requests/${id}/reject`, { method: 'POST', body: JSON.stringify({ comment }) }),

  claimRequest: (id: string) => request<{ ok: true }>(`/api/requests/${id}/claim`, { method: 'POST' }),
  releaseRequest: (id: string) => request<{ ok: true }>(`/api/requests/${id}/release`, { method: 'POST' }),

  summary: () => request<Summary>('/api/summary'),

  itQueue: () => request<{ requests: ItQueueRow[]; pdfAvailable: boolean }>('/api/requests/it-queue'),

  assignRequest: (id: string) => request<{ ok: true }>(`/api/requests/${id}/assign`, { method: 'POST' }),

  assignRequestsBulk: (ids: string[]) =>
    request<{ assigned: string[]; failed: { id: string; message: string }[] }>('/api/requests/it-queue/assign-bulk', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  saveImplementation: (
    id: string,
    patch: Partial<{
      technicianName: string | null;
      createdEmailAddress: string | null;
      implementationNotes: string | null;
      doneItemIds: string[];
      returns: { catalogItemId: string; returnStatus: ReturnStatus | null; returnCondition: string | null }[];
    }>,
  ) =>
    request<{ ok: true }>(`/api/requests/${id}/implementation`, { method: 'PATCH', body: JSON.stringify(patch) }),

  completeRequest: (id: string, signature: string) =>
    request<{ ok: true }>(`/api/requests/${id}/complete`, { method: 'POST', body: JSON.stringify({ signature }) }),

  documentUrl: (id: string) => `/api/requests/${id}/document`,
  pdfUrl: (id: string) => `/api/requests/${id}/document?format=pdf`,
};
